import { useTheme } from "@emotion/react";
import { Email, Lock, PersonAdd, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Alert,
  Box,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography
} from "@mui/material";
import { useState } from "react";
import { ApplicationButton } from "../Components";
import { useMicrospecThemeLanguageContext } from "../Themes";

const LoginEmailRegistration = ({
  identifier,
  newPassword,
  confirmPassword,
  error,
  loading,
  onNewPasswordChange,
  onConfirmPasswordChange,
  onSubmit,
  onBackToIdentifier
}) => {
  const { localizationProvider } = useMicrospecThemeLanguageContext();
  const theme = useTheme();
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Generate gradients from theme colors
  const primaryGradient = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;

  const handleClickShowNewPassword = () => setShowNewPassword(!showNewPassword);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);
  const handleMouseDownPassword = (event) => event.preventDefault();

  return (
    <Box component="form" onSubmit={onSubmit} noValidate>
      {/* Registration Notice */}
      <Alert 
        severity="info" 
        sx={{ 
          mb: 3, 
          borderRadius: 2,
          backgroundColor: `${theme.palette.primary.main}08`,
          border: `1px solid ${theme.palette.primary.main}20`,
          '& .MuiAlert-icon': {
            color: theme.palette.primary.main
          }
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {localizationProvider.translateByType({
            type: "Internal",
            text: "newAccountCreationNotice"
          }) || "سيتم إنشاء حساب جديد بهذا البريد الإلكتروني"}
        </Typography>
      </Alert>

      {/* Disabled Email Field */}
      <TextField
        margin="normal"
        fullWidth
        id="identifier"
        name="identifier"
        label={localizationProvider.translateByType({
          type: "Internal", 
          text: "emailAddress"
        }) || "البريد الإلكتروني"}
        type="email"
        autoComplete="email"
        dir="rtl"
        variant="outlined"
        value={identifier}
        disabled
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Email sx={{ color: 'action.disabled' }} />
            </InputAdornment>
          ),
        }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              backgroundColor: 'rgba(0,0,0,0.05)',
              '&.Mui-disabled': {
                '& fieldset': {
                  borderColor: 'action.disabled',
                },
              },
            },
        }}
      />

      {/* New Password Input */}
      <TextField
        margin="normal"
        required
        fullWidth
        id="newPassword"
        name="newPassword"
        label={localizationProvider.translateByType({
          type: "Internal",
          text: "enterNewPassword"
        })}
        type={showNewPassword ? 'text' : 'password'}
        autoComplete="new-password"
        autoFocus
        dir="rtl"
        variant="outlined"
        value={newPassword}
        onChange={(e) => onNewPasswordChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock sx={{ color: 'action.active' }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowNewPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {showNewPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Confirm Password Input */}
      <TextField
        margin="normal"
        required
        fullWidth
        id="confirmPassword"
        name="confirmPassword"
        label={localizationProvider.translateByType({
          type: "Internal",
          text: "confirmNewPassword"
        })}
        type={showConfirmPassword ? 'text' : 'password'}
        autoComplete="new-password"
        dir="rtl"
        variant="outlined"
        value={confirmPassword}
        onChange={(e) => onConfirmPasswordChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock sx={{ color: 'action.active' }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowConfirmPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Password Requirements */}
      <Typography 
        variant="caption" 
        color="text.secondary" 
        sx={{ 
          display: 'block', 
          mb: 2, 
          fontSize: '0.75rem',
          lineHeight: 1.4
        }}
      >
        {localizationProvider.translateByType({
          type: "Internal",
          text: "passwordRequirements"
        })}
      </Typography>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Create Account Button */}
      <ApplicationButton
        type="submit"
        fullWidth
        variant="contained"
        disabled={loading || !newPassword.trim() || !confirmPassword.trim()}
        startIcon={<PersonAdd />}
        sx={{
          mt: 2,
          mb: 2,
          py: 1.8,
          borderRadius: 3,
          fontSize: '1rem',
          fontWeight: 700,
          textTransform: 'none',
          background: primaryGradient,
          boxShadow: `0 8px 32px ${theme.palette.primary.main}30`,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 12px 40px ${theme.palette.primary.main}40`,
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '&:disabled': {
            background: 'action.disabledBackground',
            color: 'action.disabled',
            transform: 'none',
            boxShadow: 'none',
          },
        }}
      >
        {loading 
          ? (localizationProvider.translateByType({
              type: "Internal",
              text: "creatingAccount"
            }))
          : (localizationProvider.translateByType({
              type: "Internal",
              text: "createAccount"
            }))
        }
      </ApplicationButton>

      {/* Back to Email Selection Link */}
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={onBackToIdentifier}
          sx={{
            textDecoration: 'none',
            fontWeight: 500,
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {localizationProvider.translateByType({
            type: "Internal",
            text: "backToEmailSelection"
          })}
        </Link>
      </Box>
    </Box>
  );
};

export default LoginEmailRegistration;